import { getLogger } from '@onefootprint/logger';
import type { DataIdentifier, Document, Entity, VaultKind } from '@onefootprint/request-types/dashboard';
import { useToast } from '@onefootprint/ui';
import { useCallback, useEffect } from 'react';
import type { VaultType } from '@/entities/hooks/use-entity-vault';
import isDiDecryptable from '@/utils/is-di-decryptable';
import useDecryptControls from '../use-decrypt-controls';
import { AUTO_DECRYPT_DURATION, allFields, sensitiveFieldPrefixes } from './constants';
import { clearExpirationTimer, expirationTimers } from './expiration-timer';
import { enabledSelector, initialState, useBusinessAutoDecryptStore, usePersonAutoDecryptStore } from './store';
const {
  logError
} = getLogger({
  location: 'use-auto-decrypt-controls'
});
type GeneralField = 'investor_profile' | 'custom' | 'document' | 'card' | 'bank';
export type AutoDecryptField = DataIdentifier | GeneralField;
const useAutoDecryptControls = (vaultKind: VaultKind) => {
  const getStore = () => {
    return vaultKind === 'person' ? usePersonAutoDecryptStore : useBusinessAutoDecryptStore;
  };
  const store = getStore().getState();
  const set = getStore().setState;
  const get = getStore().getState;
  const decryptControls = useDecryptControls();
  const {
    toast
  } = useToast();
  const handleExpirationTimer = (expiresAt: number) => {
    const timeRemaining = expiresAt - Date.now();
    if (timeRemaining <= 0) {
      reset();
    } else {
      // Clear any existing timer for this vault kind
      clearExpirationTimer(vaultKind);
      // Set a new timer and store it in the shared reference
      const timerKey = vaultKind === 'person' ? 'person' : 'business';
      expirationTimers[timerKey] = setTimeout(() => {
        toast({
          title: 'Auto-decrypt disabled',
          description: `The auto-decrypt session for ${vaultKind === 'person' ? 'User' : 'Business'} vaults has expired`
        });
        reset();
      }, timeRemaining);
    }
  };
  useEffect(() => {
    if (store.enabled && store.expiresAt) {
      handleExpirationTimer(store.expiresAt);
    } else {
      clearExpirationTimer(vaultKind);
    }
    return () => clearExpirationTimer(vaultKind);
  }, [store.enabled, store.expiresAt, vaultKind]);
  const isSensitiveField = (field: AutoDecryptField) => sensitiveFieldPrefixes.some(prefix => field.startsWith(prefix));
  const isEntityAutoDecrypting = (fpId: string | undefined) => {
    if (!fpId) return false;
    const {
      enabled,
      fpIdsBeingDecrypted
    } = get();
    const isDecrypting = fpIdsBeingDecrypted[fpId] === true;
    return enabled && isDecrypting;
  };
  const isPersonEnabled = usePersonAutoDecryptStore(enabledSelector);
  const isBusinessEnabled = useBusinessAutoDecryptStore(enabledSelector);
  const isEnabledForKind = (kind: VaultKind) => {
    return kind === 'person' ? isPersonEnabled : isBusinessEnabled;
  };
  const reset = () => {
    set(initialState);
  };
  const enableAutoDecrypt = () => set({
    enabled: true,
    expiresAt: Date.now() + AUTO_DECRYPT_DURATION
  });
  const disableAutoDecrypt = () => reset();
  const setReason = (reason: string) => set({
    reason
  });
  const setFields = (fields: AutoDecryptField[]) => set({
    fields
  });
  const getDisFromDocuments = ({
    documents,
    entity,
    vaultData
  }: {
    documents: Document[];
    entity: Entity;
    vaultData: VaultType;
  }) => {
    const uploads = documents.flatMap(d => d.uploads);
    const imageDIs = uploads.filter(({
      identifier
    }) => isDiDecryptable(entity, identifier)).map(({
      identifier: identifier_0,
      version
    }) => `${identifier_0}:${version}` as DataIdentifier);
    const textDIs = documents.flatMap(({
      completedVersion,
      kind: kind_0,
      sambaActivityHistoryCompletedVersion
    }) => {
      if (!completedVersion) {
        return [];
      }
      const documentDataKinds = Object.keys(vaultData.dataKinds).filter(key => key.startsWith(`document.${kind_0}`)).filter(di => isDiDecryptable(entity, di as DataIdentifier));
      return documentDataKinds.map(di_0 => {
        const isSambaActivityDI = di_0.includes('samba_activity_history_response');
        const useSambaVersion = isSambaActivityDI && sambaActivityHistoryCompletedVersion;
        const version_0 = useSambaVersion ? sambaActivityHistoryCompletedVersion : completedVersion;
        return `${di_0}:${version_0}` as DataIdentifier;
      });
    });
    return [...new Set([...imageDIs, ...textDIs])];
  };
  const getDIsFromFields = ({
    entity: entity_0,
    fields: fields_0,
    documents: documents_0,
    vaultData: vaultData_0
  }: {
    entity: Entity;
    fields: AutoDecryptField[];
    documents: Document[];
    vaultData: VaultType;
  }) => {
    const dis = fields_0.filter(field_0 => field_0 !== 'document').flatMap(field_1 => {
      if (field_1 === 'investor_profile' || field_1 === 'custom' || field_1 === 'card' || field_1 === 'bank') {
        return entity_0.data.filter(({
          identifier: identifier_1
        }) => identifier_1.startsWith(field_1)).map(({
          identifier: identifier_2
        }) => identifier_2);
      }
      return field_1;
    }).filter(di_1 => isDiDecryptable(entity_0, di_1));
    if (fields_0.includes('document')) {
      const documentDIs = getDisFromDocuments({
        documents: documents_0,
        entity: entity_0,
        vaultData: vaultData_0
      });
      return [...dis, ...documentDIs];
    }
    return dis;
  };

  // biome-ignore lint/plugin: empty deps deliberately freeze the first-render closures; the compiler would re-close over fresh values each render
  const decrypt = useCallback(async ({
    entity: entity_1,
    documents: documents_1,
    vaultData: vaultData_1
  }: {
    entity: Entity;
    documents: Document[];
    vaultData: VaultType;
  }) => {
    const {
      enabled: enabled_0,
      expiresAt: expiresAt_0,
      fields: fields_1,
      reason: reason_0
    } = get();
    if (!enabled_0 || !entity_1) {
      return;
    }
    if (expiresAt_0 && Date.now() > expiresAt_0) {
      reset();
      return;
    }
    const dis_0 = getDIsFromFields({
      entity: entity_1,
      documents: documents_1,
      fields: fields_1,
      vaultData: vaultData_1
    });
    if (dis_0.length > 0) {
      set(state => ({
        ...state,
        fpIdsBeingDecrypted: {
          ...state.fpIdsBeingDecrypted,
          [entity_1.id]: true
        }
      }));
      try {
        const response = await decryptControls.decrypt({
          entity: entity_1,
          fields: dis_0,
          reason: `Auto-decryption: ${reason_0}`
        });
        return response;
      } finally {
        set(state => {
          const {
            [entity_1.id]: _removed,
            ...updatedFpIds
          } = state.fpIdsBeingDecrypted;
          return {
            ...state,
            fpIdsBeingDecrypted: updatedFpIds
          };
        });
      }
    } else {
      logError('Vault decryption error', undefined, {
        entityId: entity_1.id,
        fields: fields_1.join(','),
        detail: 'empty return from getDIsFromFields in auto-decryption'
      });
    }
    return {};
  }, []);
  return {
    ...store,
    allFields,
    sensitiveFieldPrefixes,
    isSensitiveField,
    isEntityAutoDecrypting,
    isEnabledForKind,
    enableAutoDecrypt,
    disableAutoDecrypt,
    setReason,
    setFields,
    decrypt
  };
};
export default useAutoDecryptControls;